import { createInstance as createI18nextInstance, type InitOptions } from "i18next";
import HttpBackend from "i18next-http-backend";
import type React from "react";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { I18N_LOCALE_KEY, I18N_REGION_KEY } from "./globals";
import { v4 as uuidv4 } from "uuid";
import { RegionProvider } from "./RegionContext";

export type UniversalCreateInstanceParams = {
  path: string;
  namespace: string[];
  supportedLocales: string[];
  fallbackLocale: string;
};

export function createI18nProvider<T>(
  args: UniversalCreateInstanceParams,
  options?: InitOptions<T>,
) {
  const locale = window[I18N_LOCALE_KEY];
  const region = window[I18N_REGION_KEY];

  const i18next = createI18nextInstance();
  i18next.use(initReactI18next).use(HttpBackend);
  const appVersion = window.__reactRouterManifest?.version ?? uuidv4();

  // Start initialization but don't await it - i18next will initialize in the background
  // The I18nextProvider will handle the async initialization gracefully
  i18next.init({
    backend: {
      loadPath: `/${args.path}/{{lng}}/{{ns}}.json?version=${appVersion}`,
    },
    lng: locale,
    ns: args.namespace,
    supportedLngs: args.supportedLocales,
    fallbackLng: args.fallbackLocale,

    react: {
      useSuspense: false,
    },

    ...options,
  });

  return function I18nProviderComponent(props: { children: React.ReactNode }) {
    return (
      <I18nextProvider i18n={i18next}>
        <RegionProvider value={region}>{props.children}</RegionProvider>
      </I18nextProvider>
    );
  };
}
